import type { FeatureKey } from "@/shared/entitlements/entitlements.constants";
import { useEntitlements } from "@/shared/entitlements/entitlements.context";
import type { Entitlements } from "@/shared/entitlements/schemas/entitlements.schema";

/// Pure resolver, extracted so it's testable without rendering a provider
/// (`entitlements.context.tsx` supplies `{}` for `features` while the query
/// is loading, so this naturally reads as enabled during that window too).
/// Server is the real gate (SEC2): this only drives UI affordances (locked
/// nav items, disabled buttons) and defaults to permissive so nothing
/// flickers into a locked state before the entitlements query resolves.
/// Use this for UI; use `resolveFeatureConfirmed` to gate a network request.
export function resolveFeatureEnabled(
  features: Entitlements["features"],
  key: FeatureKey,
): boolean {
  return features[key]?.enabled ?? true;
}

export function useFeatureEnabled(key: FeatureKey): boolean {
  const { features } = useEntitlements();
  return resolveFeatureEnabled(features, key);
}

/// Strict sibling for gating NETWORK REQUESTS: false until the plan has loaded
/// and explicitly enables the feature. The permissive resolver above reads
/// `{}` (still loading) as enabled, so a request gated on it fires before the
/// plan is known and the server refuses it with PLAN_UPGRADE_REQUIRED.
export function resolveFeatureConfirmed(
  features: Entitlements["features"],
  key: FeatureKey,
): boolean {
  return features[key]?.enabled === true;
}

export function useFeatureConfirmed(key: FeatureKey): boolean {
  const { features } = useEntitlements();
  return resolveFeatureConfirmed(features, key);
}
